"use client";

import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "../redux/notificationsThunks";
import { resetNotifications } from "../redux/notificationsSlice";

import {
  selectLatestNotifications,
  selectNotificationsStatus,
  selectNotificationsError,
  selectUnreadCount,
} from "../redux/notificationsSelectors";

import useNotificationsSocket from "./useNotificationsSocket";
// import { selectEffectiveUserId } from "../../sous-profile/redux/selectors/sousProfileSelectors";

import {
  selectEffectiveUserId,
  selectPrincipalUserId,
} from "../../sous-profile/redux/selectors/sousProfileSelectors";

import { selectAuthUser } from "../../authPatient/redux/selectors/authSelectors";

function selectNotifUserId(state, role) {
  if (role === "doctor") {
    const u = state?.doctor?.user || state?.doctor?.currentUser || null;
    return u?.id || u?.userId || u?.uuid || u?.pgId || u?.doctorId || null;
  }

  // if (role === "patient") {
  //   const effectiveUserId = selectEffectiveUserId(state);
  //   if (effectiveUserId) return effectiveUserId;

  //   const u = selectAuthUser(state) || state?.user?.currentUser || null;
  //   return u?.id || u?.userId || u?.uuid || u?.pgId || null;
  // }

  if (role === "patient") {
  const principalUserId = selectPrincipalUserId(state);

  if (principalUserId) {
    return principalUserId;
  }

  const u = selectAuthUser(state) || state?.user?.currentUser || null;

  return (
    u?.id ||
    u?.userId ||
    u?.uuid ||
    u?.pgId ||
    null
  );
}

  const doctorUser = state?.doctor?.user || state?.doctor?.currentUser || null;
  if (doctorUser) {
    return (
      doctorUser?.id ||
      doctorUser?.userId ||
      doctorUser?.uuid ||
      doctorUser?.pgId ||
      doctorUser?.doctorId ||
      null
    );
  }

  const patientUser =
    selectAuthUser(state) ||
    state?.user?.currentUser ||
    null;

  return (
    selectEffectiveUserId(state) ||
    patientUser?.id ||
    patientUser?.userId ||
    patientUser?.uuid ||
    patientUser?.pgId ||
    null
  );
}

function normalizeUserId(value) {
  if (value == null) return null;
  const s = String(value).trim();
  return s ? s : null;
}

export default function useNotifications({
  pageSize = 20,
  latestCount = 10,
  enableWs = false,
  autoFetch = true,
  role,
  userId: userIdOverride,
} = {}) {
  const dispatch = useDispatch();

  const status = useSelector(selectNotificationsStatus);
  const error = useSelector(selectNotificationsError);
  const unreadCount = useSelector(selectUnreadCount);

  const storeUserId = useSelector((state) => selectNotifUserId(state, role));
  const userId = normalizeUserId(userIdOverride) || storeUserId;
  const notificationsUserId = useSelector((state) => state?.notifications?.userId);

  const latestSelector = useMemo(
    () => selectLatestNotifications(latestCount),
    [latestCount]
  );
  const notifications = useSelector(latestSelector);

  const { handleNotificationClick } = useNotificationsSocket({
    enabled: !!enableWs,
    roleOverride: role,
    userIdOverride: userId,
  });

  // ✅ fetch only when userId exists
  useEffect(() => {
    if (String(notificationsUserId || "") !== String(userId || "")) {
      dispatch(resetNotifications());
    }
    if (!userId) {
      return;
    }
    if (!autoFetch) return;


    dispatch(fetchNotifications({ skip: 0, limit: pageSize, userId, role }))
      .unwrap?.()
      .then((res) => {
      })
      .catch(() => {});
  }, [dispatch, pageSize, autoFetch, latestCount, role, userId, notificationsUserId]);

  const refresh = useCallback(() => {
    if (!userId) {
      return Promise.resolve();
    }
    return dispatch(fetchNotifications({ skip: 0, limit: pageSize, userId, role }));
  }, [dispatch, pageSize, userId, role]);

  const markRead = useCallback(
    async (id) => {
      if (!id) return;
      return dispatch(markNotificationRead({ id: String(id), userId, role }));
    },
    [dispatch, userId, role]
  );

  const markAllRead = useCallback(() => {
    return dispatch(markAllNotificationsRead({ userId, role }));
  }, [dispatch, userId, role]);

  const isLoading = status === "loading";
  const hasUnread = unreadCount > 0;

  return useMemo(
    () => ({
      notifications,
      unreadCount,
      hasUnread,
      isLoading,
      status,
      error,
      refresh,
      markRead,
      markAllRead,
      handleNotificationClick,
      userId, // debug utile
    }),
    [
      notifications,
      unreadCount,
      hasUnread,
      isLoading,
      status,
      error,
      refresh,
      markRead,
      markAllRead,
      handleNotificationClick,
      userId,
    ]
  );
}
